// Import Dependencies
import PropTypes from "prop-types";
import { forwardRef } from "react";
import clsx from "clsx";

// Local Imports
import { COLORS } from "app/presentation/constants/app.constant";
import { setThisClass } from "app/presentation/utils/setThisClass";

// ----------------------------------------------------------------------

const variants = {
    filled: "text-white bg-this",
    outlined:
        "border border-this/30 text-this dark:border-this-lighter/30 dark:text-this-lighter",
    soft: "text-this-darker bg-this-darker/[0.07] dark:text-this-lighter dark:bg-this-lighter/10",
};

const neutralVariants = {
    filled: "bg-gray-200 text-gray-900 dark:bg-surface-2 dark:text-dark-50",
    outlined:
        "border border-gray-300 text-gray-900 dark:border-surface-1 dark:text-dark-50",
    soft: "bg-gray-200/30 text-gray-900 dark:bg-dark-500/30 dark:text-dark-50",
};

const Alert = forwardRef((props, ref) => {
    const {
        component,
        className,
        unstyled,
        variant = "filled",
        color,
        icon: Icon,
        title,
        children,
        ...rest
    } = props;

    const Component = component || "div";
    const mergedColor = color || "neutral";

    return (
        <Component
            className={clsx(
                "alert-base p-4 rounded-lg",
                !unstyled
                    ? [
                        "alert",
                        mergedColor === "neutral"
                            ? neutralVariants[variant]
                            : [
                                setThisClass(mergedColor),
                                variants[variant],
                            ],
                    ]
                    : color && color !== "neutral" && setThisClass(color),
                className,
            )}
            ref={ref}
            {...rest}
        >
            <div className="flex items-start gap-3">
                {Icon && (
                    <div className="flex-shrink-0">
                        <Icon className="w-5 h-5" />
                    </div>
                )}
                <div className="flex-1">
                    {title && (
                        <h4 className="font-semibold text-sm mb-1">{title}</h4>
                    )}
                    {children && (
                        <div className="text-sm">{children}</div>
                    )}
                </div>
            </div>
        </Component>
    );
});

Alert.displayName = "Alert";

Alert.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    component: PropTypes.elementType,
    variant: PropTypes.oneOf(["filled", "outlined", "soft"]),
    color: PropTypes.oneOf(COLORS),
    unstyled: PropTypes.bool,
    icon: PropTypes.elementType,
    title: PropTypes.string,
};

export { Alert };